/**
 * BusinessVerificationBanner — provider dashboard banner.
 *
 * Phase 1c. Renders one of four states based on the caller's
 * poopartner intake status:
 *   * `submitted` / `under_review` — neutral info: "Your business
 *     verification is under review."
 *   * `approved` — success: "Your business is verified." + dismiss
 *   * `rejected` — warning: link to /partner/intake/status
 *   * no intake row (404) — info CTA: "Complete your business
 *     verification to unlock features." + link to /partner/business-intake
 *
 * Fetches `/api/poopartner/intake/me` on mount. Renders nothing until
 * the fetch settles (avoids layout jump).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Clock, AlertTriangle, ShieldCheck, X } from "lucide-react";

export default function BusinessVerificationBanner() {
  const [state, setState] = useState({ loading: true, intake: null, hasNoIntake: false });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/poopartner/intake/me");
        if (!cancelled) setState({ loading: false, intake: data, hasNoIntake: false });
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 404) {
          setState({ loading: false, intake: null, hasNoIntake: true });
        } else {
          setState({ loading: false, intake: null, hasNoIntake: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.loading || dismissed) return null;

  // No intake yet — CTA
  if (state.hasNoIntake) {
    return (
      <div
        className="rounded border border-pp-border bg-pp-surface-2 p-3 flex items-center gap-2 mb-4"
        data-testid="business-verification-banner-none"
      >
        <ShieldCheck size={18} className="text-pp-purple" aria-hidden="true" />
        <p className="text-sm text-pp-text flex-1">
          Complete your business verification to unlock features.
        </p>
        <Link
          to="/partner/business-intake"
          className="text-sm underline text-pp-orange"
          data-testid="business-verification-banner-start-link"
        >
          Start now
        </Link>
      </div>
    );
  }

  const intake = state.intake;
  if (!intake) return null;

  if (intake.status === "submitted" || intake.status === "under_review") {
    return (
      <div
        className="rounded border border-pp-border bg-pp-surface-2 p-3 flex items-center gap-2 mb-4"
        data-testid="business-verification-banner-pending"
      >
        <Clock size={18} className="text-pp-text-muted" aria-hidden="true" />
        <p className="text-sm text-pp-text flex-1">
          Your business verification is under review.
        </p>
        <Link
          to="/partner/intake/status"
          className="text-sm underline text-pp-orange"
          data-testid="business-verification-banner-view-link"
        >
          View status
        </Link>
      </div>
    );
  }

  if (intake.status === "approved") {
    return (
      <div
        className="rounded border border-emerald-200 bg-emerald-50 p-3 flex items-center gap-2 mb-4"
        data-testid="business-verification-banner-approved"
      >
        <CheckCircle2 size={18} className="text-emerald-700" aria-hidden="true" />
        <p className="text-sm text-emerald-800 flex-1">
          Your business is verified.
        </p>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-emerald-700 hover:text-emerald-900"
          aria-label="Dismiss"
          data-testid="business-verification-banner-dismiss-btn"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  if (intake.status === "rejected") {
    return (
      <div
        className="rounded border border-amber-300 bg-amber-50 p-3 flex items-center gap-2 mb-4"
        data-testid="business-verification-banner-rejected"
      >
        <AlertTriangle size={18} className="text-amber-700" aria-hidden="true" />
        <p className="text-sm text-amber-900 flex-1">
          Your application was not approved. See details.
        </p>
        <Link
          to="/partner/intake/status"
          className="text-sm underline text-amber-900"
          data-testid="business-verification-banner-rejected-link"
        >
          View
        </Link>
      </div>
    );
  }

  return null;
}
